import axios from 'axios';
import {
  Shift,
  Booking,
  ShiftTag,
  User,
  Role,
  Constraint,
  BookingStats,
  ShiftStats,
  UsersByRole,
  SickNote,
  WithdrawalRequest,
  NewsPost,
} from '../types/index';

const API = axios.create({
  baseURL: '/api',
});

// Add token to requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authService = {
  requestLogin: (email: string) => API.post('/auth/login', { email }),
  validateToken: (token: string) => API.post('/auth/validate-token', { token }),
  getCurrentUser: () => API.get<User>('/auth/me'),
  confirmWhatsAppGroupJoin: (code: string) => API.post<{ user: User }>('/auth/whatsapp-group/confirm', { code }),
  logEvent: (eventType: string, details?: Record<string, any>) =>
    API.post('/auth/log-event', { event_type: eventType, details }),
};

export const shiftsService = {
  getAllShifts: () => API.get<Shift[]>('/shifts'),
  getNews: () => API.get<NewsPost[]>('/shifts/news'),
  getShiftById: (id: number) => API.get<Shift>(`/shifts/${id}`),
  getTags: () => API.get<ShiftTag[]>('/shifts/tags/all'),
  exportShiftPlan: () => API.get<{ version: number; exported_at: string; shift_count: number; shifts: any[] }>('/shifts/export-plan'),
  importShiftPlan: (payload: { shifts: any[]; replace_existing?: boolean; create_missing_tags?: boolean }) =>
    API.post<{ message: string; replace_existing: boolean; created: number; updated: number; skipped: number; created_tags: number; errors: string[] }>('/shifts/import-plan', payload),
  getSettings: () => API.get<{ event_name?: string; show_shift_title?: boolean; show_shift_location?: boolean; show_next_shift_box?: boolean; show_available_spots?: boolean; show_waitlist_position?: boolean; volunteer_login_waitlist_modal_enabled?: boolean; volunteer_login_new_signups_closed?: boolean; whatsapp_group_join_url?: string; whatsapp_group_confirmation_code?: string; booking_enabled?: 'no-access' | 'view-only' | 'booking-enabled'; sick_note_enabled?: boolean; withdrawal_permitted?: boolean; booking_deadline?: string | null; withdrawal_deadline?: string | null; booking_capacity_percent?: number; timezone?: string; peoplevol_portal_url?: string }>('/shifts/settings'),
  createShift: (shift: any) => API.post('/shifts', shift),
  bulkCreateShifts: (data: {
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    title: string;
    description?: string;
    location?: string;
    tags?: number[];
    role_requirements?: Array<{ role_name: string; max_count: number }>;
  }) => API.post('/shifts/bulk-create', data),
  updateShift: (id: number, shift: any) => API.put(`/shifts/${id}`, shift),
  deleteShift: (id: number) => API.delete(`/shifts/${id}`),
};

export const bookingsService = {
  getUserBookings: () => API.get<Booking[]>('/bookings'),
  bookShift: (shiftId: number) => API.post('/bookings', { shift_id: shiftId }),
  bookMultipleShifts: (shiftIds: number[]) => API.post('/bookings/bulk', { shift_ids: shiftIds }),
  cancelBooking: (bookingId: number) => API.delete(`/bookings/${bookingId}`),
  checkConstraints: () => API.get('/bookings/constraints/check'),
  getConstraints: () => API.get<any[]>('/bookings/constraints'),
  validateSelection: (shiftIds: number[]) => API.post<any>('/bookings/validate', { shift_ids: shiftIds }),
};

export const adminService = {
  getRoles: () => API.get<Role[]>('/admin/roles'),
  getUsers: () => API.get<User[]>('/admin/users'),
  getUser: (id: number) => API.get<User & { application_status?: string; full_name?: string; phone?: string; date_of_birth?: string; dtu_member?: boolean; dtu_member_status?: 'yes_student_employee' | 'yes_alumni' | 'no'; previous_volunteer?: string; has_peoplevol_profile?: boolean | null; confirmed_peoplevol_registration?: boolean | null }>('/admin/users/' + id),
  getUserByEmail: (email: string) => API.get<User & { application_status?: string; full_name?: string; phone?: string; date_of_birth?: string; dtu_member?: boolean; dtu_member_status?: 'yes_student_employee' | 'yes_alumni' | 'no'; previous_volunteer?: string; has_peoplevol_profile?: boolean | null; confirmed_peoplevol_registration?: boolean | null }>(`/admin/users/by-email?email=${encodeURIComponent(email)}`),
  updateUserRole: (id: number, role: User['role']) =>
    API.put(`/admin/users/${id}/role`, { role }),
  updateUser: (id: number, payload: { role?: User['role']; name?: string; withdrawn?: boolean; require_doctors_note?: boolean; not_found_on_whatsapp?: boolean; application_status?: string; full_name?: string; phone?: string; date_of_birth?: string; dtu_member?: boolean; dtu_member_status?: 'yes_student_employee' | 'yes_alumni' | 'no'; previous_volunteer?: string }) =>
    API.put(`/admin/users/${id}`, payload),
  updateUserDoctorNote: (id: number, require_doctors_note: boolean) =>
    API.put(`/admin/users/${id}/doctor-note`, { require_doctors_note }),
  deleteUser: (id: number) => API.delete(`/admin/users/${id}`),
  importUsers: (payload: { csvText: string }) => API.post('/admin/users/import', payload),
  importWaitlistUsers: (payload: { csvText: string }) => API.post('/admin/waitlist/import', payload),

  getConstraints: () => API.get<Constraint[]>('/admin/constraints'),
  createConstraint: (constraint: Partial<Constraint>) =>
    API.post('/admin/constraints', constraint),
  updateConstraint: (id: number, constraint: Partial<Constraint>) =>
    API.put(`/admin/constraints/${id}`, constraint),
  deleteConstraint: (id: number) => API.delete(`/admin/constraints/${id}`),
  deleteAllShifts: () => API.delete('/admin/shifts/all'),
    deleteInactiveUsers: () => API.delete('/admin/users/inactive/all'),
  deleteNonAdminUsers: () => API.delete('/admin/users/non-admin/all'),

  updateGlobalSettings: (settings: { min_hours_between_shifts: number; event_name?: string; show_shift_title?: boolean; show_shift_location?: boolean; show_next_shift_box?: boolean; show_available_spots?: boolean; show_waitlist_position?: boolean; volunteer_login_waitlist_modal_enabled?: boolean; volunteer_login_new_signups_closed?: boolean; whatsapp_group_join_url?: string; whatsapp_group_confirmation_code?: string; booking_enabled?: 'no-access' | 'view-only' | 'booking-enabled'; sick_note_enabled?: boolean; withdrawal_permitted?: boolean; fill_threshold_critical?: number; fill_threshold_warning?: number; fill_threshold_low?: number; booking_capacity_percent?: number; booking_deadline?: string | null; withdrawal_deadline?: string | null; waitlist_offer_hours?: number; notification_application_submitted?: boolean; notification_application_approved?: boolean; notification_application_moved_to_waitlist?: boolean; notification_waitlist_offer_sent?: boolean; notification_sick_note_submitted?: boolean; notification_sick_note_status?: boolean; notification_withdrawal_approved?: boolean; notification_template_application_submitted?: string | null; notification_template_application_approved?: string | null; notification_template_application_moved_to_waitlist?: string | null; notification_template_waitlist_offer_sent?: string | null; notification_template_sick_note_submitted?: string | null; notification_template_sick_note_status?: string | null; notification_template_withdrawal_approved?: string | null; notification_template_shifts_updated?: string | null; peoplevol_portal_url?: string; mailchimp_api_key?: string; mailchimp_audience_id?: string; mailchimp_server_prefix?: string; mailchimp_sync_tag?: string }) =>
    API.put('/admin/settings', settings),
  getGlobalSettings: () => API.get<{ min_hours_between_shifts: number; event_name?: string; show_shift_title?: boolean; show_shift_location?: boolean; show_next_shift_box?: boolean; show_available_spots?: boolean; show_waitlist_position?: boolean; volunteer_login_waitlist_modal_enabled?: boolean; volunteer_login_new_signups_closed?: boolean; whatsapp_group_join_url?: string; whatsapp_group_confirmation_code?: string; booking_enabled?: 'no-access' | 'view-only' | 'booking-enabled'; sick_note_enabled?: boolean; withdrawal_permitted?: boolean; fill_threshold_critical?: number; fill_threshold_warning?: number; fill_threshold_low?: number; booking_capacity_percent?: number; booking_deadline?: string | null; withdrawal_deadline?: string | null; waitlist_offer_hours?: number; notification_application_submitted?: boolean; notification_application_approved?: boolean; notification_application_moved_to_waitlist?: boolean; notification_waitlist_offer_sent?: boolean; notification_sick_note_submitted?: boolean; notification_sick_note_status?: boolean; notification_withdrawal_approved?: boolean; notification_template_application_submitted?: string | null; notification_template_application_approved?: string | null; notification_template_application_moved_to_waitlist?: string | null; notification_template_waitlist_offer_sent?: string | null; notification_template_sick_note_submitted?: string | null; notification_template_sick_note_status?: string | null; notification_template_withdrawal_approved?: string | null; notification_template_shifts_updated?: string | null; peoplevol_portal_url?: string; mailchimp_api_key?: string; mailchimp_audience_id?: string; mailchimp_server_prefix?: string; mailchimp_sync_tag?: string }>('/admin/settings'),
  syncMailchimpActive: (payload?: { dry_run?: boolean }) => API.post<{ total: number; tag?: string; dry_run?: boolean; synced: number; failed: number; removed?: number; removal_failed?: number; would_sync?: number; would_remove?: number; errors: Array<{ email: string; error: string }> }>('/admin/mailchimp/sync-active', payload || {}),

  createTag: (payload: { name: string; description?: string }) =>
    API.post('/admin/tags', payload),
  deleteTag: (id: number) => API.delete(`/admin/tags/${id}`),

  getBookingStats: () => API.get<BookingStats>('/admin/stats/bookings'),
  getShiftStats: () => API.get<ShiftStats>('/admin/stats/shifts'),
  getUsersByRole: () => API.get<UsersByRole[]>('/admin/stats/users-by-role'),
  getAuthLogs: (params?: { limit?: number; event_type?: string; email?: string; from?: string; to?: string }) => {
    const limit = params?.limit ?? 200;
    const query = new URLSearchParams();
    query.set('limit', String(limit));
    if (params?.event_type) query.set('event_type', params.event_type);
    if (params?.email) query.set('email', params.email);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    return API.get<{ logs: Array<{ id: number; event_type: string; email?: string | null; ip_address?: string | null; details?: any; created_at: string }>; total_storage_bytes: number }>(`/admin/logs/auth?${query.toString()}`);
  },
  getUserBookingsAdmin: (userId: number) => API.get<any[]>(`/admin/users/${userId}/bookings`),
  // Assignments
  getAssignments: () => API.get<any[]>('/admin/assignments'),
  reassignVolunteer: (payload: { booking_id: number; to_shift_id: number }) =>
    API.post('/admin/assignments/reassign', payload),
  swapVolunteerAssignments: (payload: { booking_id_1: number; booking_id_2: number }) =>
    API.post('/admin/assignments/swap', payload),
  assignVolunteer: (payload: { user_id: number; shift_id: number; assigned_role?: string }) =>
    API.post('/admin/assignments/assign', payload),
  unassignVolunteer: (bookingId: number) =>
    API.delete(`/admin/assignments/${bookingId}`),
  
  // Deviations
  getDeviations: () => API.get<any[]>('/admin/deviations'),
  updateDeviation: (bookingId: number, payload: { absence_excused?: boolean | null; absence_handled?: boolean }) =>
    API.put(`/admin/deviations/${bookingId}`, payload),
  updateShiftDeviation: (shiftId: number, payload: { read: boolean }) =>
    API.put(`/admin/deviations/shifts/${shiftId}`, payload),

  // Sick notes
  getSickNotes: () => API.get<SickNote[]>('/sick-notes/admin'),
  updateSickNote: (id: number, payload: { status: SickNote['status']; admin_notes?: string; require_doctors_note?: boolean; new_shift_id?: number }) =>
    API.put(`/sick-notes/admin/${id}`, payload),
  deleteSickNote: (id: number) => API.delete(`/sick-notes/admin/${id}`),
  leaveFestival: (id: number) => API.post(`/sick-notes/admin/${id}/leave-festival`, {}),
  getUserFutureShifts: (userId: number) => API.get<any[]>(`/admin/users/${userId}/future-shifts`),
  getUserShifts: (userId: number) => API.get<any[]>(`/admin/users/${userId}/shifts`),
  updateUserApplicationStatus: (userId: number, status: string) =>
    API.put(`/admin/users/${userId}/application-status`, { status }),
  withdrawUser: (userId: number, adminNotes?: string) =>
    API.post<{ success: boolean; deleted_bookings: number }>(`/admin/users/${userId}/withdraw`, { admin_notes: adminNotes }),

  notifyShiftsUpdated: (userId: number) => API.post(`/admin/users/${userId}/notify-shifts-updated`, {}),

  // Admin to-do list
  getTodos: () => API.get<{ id: number; text: string; created_at: string }[]>('/admin/todos'),
  createTodo: (text: string) => API.post<{ id: number; text: string; created_at: string }>('/admin/todos', { text }),
  deleteTodo: (id: number) => API.delete(`/admin/todos/${id}`),

  // Waiting list
  getWaitlist: () => API.get<User[]>('/admin/waitlist'),
  addToWaitlist: (userId: number) => API.post(`/admin/waitlist/add/${userId}`, {}),
  moveWaitlistPosition: (userId: number, newPosition: number) =>
    API.post(`/admin/waitlist/move/${userId}`, { newPosition }),
  sendWaitlistOffer: (userId: number) => API.post(`/admin/waitlist/send-offer/${userId}`, {}),
  sendBatchWaitlistOffers: (userIds: number[]) => API.post(`/admin/waitlist/batch-offer`, { userIds }),
  approveFromWaitlist: (userId: number) => API.post(`/admin/waitlist/approve/${userId}`, {}),
  removeFromWaitlist: (userId: number, newStatus?: 'rejected' | 'pending') => 
    API.delete(`/admin/waitlist/${userId}${newStatus ? `?newStatus=${newStatus}` : ''}`),
  testReduceOfferDeadline: (userId: number) => API.post(`/admin/waitlist/test/reduce-deadline/${userId}`, {}),
  confirmAcceptance: (userId: number) => API.post(`/admin/waitlist/confirm-acceptance/${userId}`, {}),
  doNotConfirmAcceptance: (userId: number) => API.post(`/admin/waitlist/do-not-confirm-acceptance/${userId}`, {}),
  cancelOffer: (userId: number) => API.post(`/admin/waitlist/cancel-offer/${userId}`, {}),
  getNewsPosts: () => API.get<NewsPost[]>('/admin/news'),
  createNewsPost: (payload: { title: string; content: string; is_published: boolean }) => API.post<NewsPost>('/admin/news', payload),
  updateNewsPost: (id: number, payload: { title: string; content: string; is_published: boolean }) => API.put<NewsPost>(`/admin/news/${id}`, payload),
  deleteNewsPost: (id: number) => API.delete(`/admin/news/${id}`),
  // Send reminder to active users without booked shifts
  sendNoShiftsReminder: (payload: { subject: string; template: string }) => API.post('/admin/notify/no-shifts', payload),
};

export const volunteerApplicationService = {
  getStatus: () => API.get<any>('/volunteer-applications/status'),
  submitApplication: (data: {
    full_name: string;
    date_of_birth: string;
    phone: string;
    dtu_member_status: 'yes_student_employee' | 'yes_alumni' | 'no';
    previous_volunteer?: string;
    terms_agreed: boolean;
    has_peoplevol_profile?: boolean | null;
    confirmed_peoplevol_registration?: boolean;
  }) => API.post('/volunteer-applications/apply', data),
  confirmRegistration: (data?: { has_peoplevol_profile?: boolean; confirmed_peoplevol_registration?: boolean }) =>
    API.post('/volunteer-applications/confirm-registration', data || {}),
  withdrawFromWaitlist: () => API.post('/volunteer-applications/withdraw-waitlist'),
  acceptWaitlistOffer: () => API.post('/volunteer-applications/accept-waitlist-offer'),
  declineWaitlistOffer: () => API.post('/volunteer-applications/decline-waitlist-offer'),
  getPendingApplications: () => API.get<any[]>('/volunteer-applications/admin/pending'),
  approveApplication: (applicationId: number, assigned_role?: string) =>
    API.post(`/volunteer-applications/admin/approve/${applicationId}`, { assigned_role }),
  rejectApplication: (applicationId: number, notes?: string) =>
    API.post(`/volunteer-applications/admin/reject/${applicationId}`, { notes }),
  sendIncompleteReminder: (applicationId: number) =>
    API.post<{ sent_at: string }>(`/volunteer-applications/admin/${applicationId}/send-incomplete-reminder`),
};

export const supervisorService = {
  getShifts: () => API.get<any[]>('/supervisor/shifts'),
  getShiftVolunteers: (shiftId: number) => API.get<any[]>(`/supervisor/shifts/${shiftId}/volunteers`),
  updateAttendance: (bookingId: number, attendance_status: string, late_by_minutes?: number) =>
    API.put(`/supervisor/attendance/${bookingId}`, { attendance_status, late_by_minutes }),
  updateVolunteerNotes: (bookingId: number, supervisor_notes: string) =>
    API.put(`/supervisor/notes/${bookingId}`, { supervisor_notes }),
  updateShiftNotes: (shiftId: number, supervisor_notes: string) =>
    API.put(`/supervisor/shifts/${shiftId}/notes`, { supervisor_notes }),
};

export const sickNoteService = {
  submitSickNote: (payload: {
    booking_id: number;
    name: string;
    phone: string;
    plan: 'reschedule' | 'leave';
    reschedule_days?: string;
    wristband_photo?: string;
    doctor_note_photo?: string;
  }) => API.post('/sick-notes', payload),
  getMine: () => API.get<{ profile: { name?: string; phone?: string; require_doctors_note?: boolean }; notes: SickNote[] }>('/sick-notes'),
  cancelSickNote: (id: number) => API.delete(`/sick-notes/${id}`),
};

export const withdrawalService = {
  submitWithdrawal: (reason: string) => API.post('/withdrawals', { reason }),
  getMine: () => API.get<WithdrawalRequest[]>('/withdrawals/mine'),
  cancelWithdrawal: (id: number) => API.delete(`/withdrawals/${id}`),
};

export const adminWithdrawalService = {
  getAll: () => API.get<WithdrawalRequest[]>('/withdrawals/admin'),
  update: (id: number, payload: { status: 'approved' | 'rejected'; admin_notes?: string; delete_shifts?: boolean }) =>
    API.put(`/withdrawals/admin/${id}`, payload),
  delete: (id: number) => API.delete(`/withdrawals/admin/${id}`),
};

